import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';
import JoditEditor from 'jodit-react';

const EditVehicle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showAmenitiesDropdown, setShowAmenitiesDropdown] = useState(false);
  const countryDropdownRef = useRef(null);
  const stateDropdownRef = useRef(null);
  const locationDropdownRef = useRef(null);
  const amenitiesDropdownRef = useRef(null);
  const [formData, setFormData] = useState({
    id: '', name: '', countryId: '', stateId: [], location: [], address: '', vehicleType: '',
    chargesKm: '', chargesDay: '', waitingCharges: '', sheets: '', luggage: '', extraCharges: '',
    fuelType: '', ac: 'Yes', smoking: 'No', amenities: [], details: '', image: null
  });
  const [imagePreview, setImagePreview] = useState('');
  const [gallery, setGallery] = useState([]);
  const [newGalleryFiles, setNewGalleryFiles] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const fileInputRef = useRef(null);
  const mediaUrl = process.env.REACT_APP_MEDIA_URL;

  const editorConfig = {
    readonly: false,
    height: 300,
    toolbar: true,
    uploader: { insertImageAsBase64URI: false },
    removeButtons: ['image', 'file']
  };

  useEffect(() => {
    fetchVehicleData();
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) setShowCountryDropdown(false);
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) setShowStateDropdown(false);
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) setShowLocationDropdown(false);
      if (amenitiesDropdownRef.current && !amenitiesDropdownRef.current.contains(event.target)) setShowAmenitiesDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (formData.countryId) {
      fetchStateLocations('country');
    }
  }, [formData.countryId]);

  useEffect(() => {
    if (formData.stateId.length > 0) {
      fetchStateLocations('state');
    }
  }, [formData.stateId]);

  const fetchVehicleData = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`editVehicle/${id}`);
      const vehicleData = response.data.data;
      
      setFormData({
        id: vehicleData._id,
        name: vehicleData.name || '',
        countryId: vehicleData.countryId || '',
        stateId: vehicleData.stateId || [],
        location: vehicleData.location || [],
        address: vehicleData.address || '',
        vehicleType: vehicleData.vehicleType || '',
        chargesKm: vehicleData.chargesKm || '',
        chargesDay: vehicleData.chargesDay || '',
        waitingCharges: vehicleData.waitingCharges || '',
        sheets: vehicleData.sheets || '',
        luggage: vehicleData.luggage || '',
        extraCharges: vehicleData.extraCharges || '',
        fuelType: vehicleData.fuelType || '',
        ac: vehicleData.ac || 'Yes',
        smoking: vehicleData.smoking || 'No',
        amenities: vehicleData.amenities || [],
        details: vehicleData.details || '',
        image: null
      });
      
      setImagePreview(vehicleData.image ? `${mediaUrl}${vehicleData.image}` : '');
      setGallery(vehicleData.gallery || []);
      setCountries(response.data.country || []);
      setStates(response.data.states || []);
      setVehicleTypes(response.data.types || []);
      
      const selectedCountry = response.data.country?.find(c => c._id === vehicleData.countryId);
      if (selectedCountry) setCountrySearch(selectedCountry.name);
    } catch (error) {
      toast.error('Failed to fetch vehicle data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStateLocations = async (type) => {
    try {
      const response = await apiService.post('stateLocationList', {
        countryId: formData.countryId,
        stateId: formData.stateId,
        type
      });
      
      if (response.message === 'success') {
        if (type === 'country') {
          setStates(response.data.states || []);
        }
        setLocations(response.data.locations || []);
      }
    } catch (error) {
      console.error('Failed to fetch locations');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be 5MB or less');
        e.target.value = '';
        return;
      }
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`File "${file.name}" is too large (max 5MB)`);
        continue;
      }
      validFiles.push(file);
    }
    
    setNewGalleryFiles(prev => [...prev, ...validFiles]);
  };

  const removeGalleryImage = async (imageName, index) => {
    try {
      await apiService.post('deleteImage', {
        type: 'package',
        typeId: id,
        imageName
      });
      setGallery(prev => prev.filter((_, i) => i !== index));
      toast.success('Image deleted successfully');
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const removeNewGalleryImage = (index) => {
    setNewGalleryFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (!formData.countryId || formData.stateId.length === 0) {
      toast.error('Select country and state first');
      return;
    }
    
    try {
      const response = await apiService.post('addVendorLocation', {
        name: locationName,
        countryId: formData.countryId,
        stateId: formData.stateId
      });
      
      if (!response.success) {
        toast.success(response.message);
        setLocations(prev => [...prev, response.data]);
        setFormData(prev => ({ ...prev, location: [...prev.location, response.data._id] }));
        setShowLocationModal(false);
        setLocationName('');
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Failed to add location');
    }
  };

  const toggleSelection = (field, value) => {
    const current = formData[field];
    const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
    setFormData({ ...formData, [field]: updated });
  };

  const getSelectedText = (field, items) => {
    const selected = items.filter(item => formData[field].includes(item._id || item));
    return selected.length > 0 ? `${selected.length} selected` : `Select ${field}`;
  };

  const filteredCountries = countries.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()));

  const handleCountryChange = async (country) => {
    setFormData({ ...formData, countryId: country._id, stateId: [], location: [] });
    setCountrySearch(country.name);
    setShowCountryDropdown(false);
    try {
      const response = await apiService.post('stateLocationList', { countryId: country._id, stateId: [], type: 'country' });
      if (response.message === 'success') {
        setStates(response.data.states || []);
        setLocations(response.data.locations || []);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const totalImages = gallery.length + newGalleryFiles.length;
    if (totalImages > 10) {
      toast.error('You can upload maximum 10 images!!');
      return;
    }
    
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'stateId' || key === 'location' || key === 'amenities') {
          formData[key].forEach(val => data.append(`${key}[]`, val));
        } else if (key !== 'image' && formData[key]) {
          data.append(key, formData[key]);
        }
      });
      
      if (formData.image) data.append('image', formData.image);
      newGalleryFiles.forEach(file => data.append('images', file));
      
      const response = await apiService.postFormData('updateVehicle', data);
      if (response.success) {
        toast.error(response.message);
      } else {
        toast.success('Vehicle updated successfully');
        navigate('/view-vehicles');
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009ef7]"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-0">
      <div className="mb-3">
        <nav className="flex items-center space-x-2 text-sm text-gray-600">
          <a href="/dashboard" className="hover:text-gray-900">Home</a>
          <span className="text-gray-400">/</span>
          <a href="/view-vehicles" className="hover:text-gray-900">Vehicles</a>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900">Edit Vehicle</span>
        </nav>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Image</label>
            <div className="relative inline-block">
              <div
                className="w-72 h-60 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden"
                style={{
                  backgroundImage: imagePreview ? `url(${imagePreview})` : 'none',
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              >
                {!imagePreview && <i className="bi bi-image text-4xl text-gray-400"></i>}
              </div>
              <label
                className="absolute bottom-2 right-2 w-6 h-6 bg-white rounded-full shadow flex items-center justify-center cursor-pointer hover:bg-gray-50"
                title="Change image"
              >
                <i className="bi bi-pencil text-xs"></i>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
            <p className="text-sm text-gray-500 mt-2">Allowed file types: png, jpg, jpeg</p>
          </div>

          <div className="col-span-7 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vehicle Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter Vehicle Name"
                className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vehicle Country <span className="text-red-500">*</span>
                </label>
                <div className="relative" ref={countryDropdownRef}>
                  <input
                    type="text"
                    value={countrySearch}
                    onChange={(e) => { setCountrySearch(e.target.value); setShowCountryDropdown(true); }}
                    onFocus={() => setShowCountryDropdown(true)}
                    placeholder="Search Country"
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                    required={!formData.countryId}
                  />
                  {showCountryDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredCountries.length > 0 ? filteredCountries.map(c => (
                        <div key={c._id} onClick={() => handleCountryChange(c)} className="px-2 sm:px-4 py-2 hover:bg-gray-100 cursor-pointer">{c.name}</div>
                      )) : <div className="px-2 sm:px-4 py-2 text-gray-500">No countries found</div>}
                    </div>
                  )}
                </div>
                <input type="hidden" name="countryId" value={formData.countryId} required />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vehicle Region <span className="text-red-500">*</span>
                </label>
                <div className="relative" ref={stateDropdownRef}>
                  <div 
                    onClick={() => setShowStateDropdown(!showStateDropdown)} 
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg cursor-pointer bg-white"
                  >
                    {getSelectedText('stateId', states)}
                  </div>
                  {showStateDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {states.map(s => (
                        <div 
                          key={s._id} 
                          onClick={() => toggleSelection('stateId', s._id)} 
                          className="px-2 sm:px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                        >
                          <input 
                            type="checkbox" 
                            checked={formData.stateId.includes(s._id)} 
                            onChange={() => {}} 
                            className="mr-2" 
                          />
                          {s.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Locations <span className="text-red-500">*</span>
                </label>
                <div className="relative" ref={locationDropdownRef}>
                  <div 
                    onClick={() => setShowLocationDropdown(!showLocationDropdown)} 
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg cursor-pointer bg-white"
                  >
                    {getSelectedText('location', locations)}
                  </div>
                  {showLocationDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {locations.map(l => (
                        <div 
                          key={l._id} 
                          onClick={() => toggleSelection('location', l._id)} 
                          className="px-2 sm:px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                        >
                          <input 
                            type="checkbox" 
                            checked={formData.location.includes(l._id)} 
                            onChange={() => {}} 
                            className="mr-2" 
                          />
                          {l.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowLocationModal(true)}
                className="px-2 sm:px-4 py-2 text-white rounded-lg self-end"
                style={{ backgroundColor: '#009ef7' }}
              >
                <i className="bi bi-plus-lg"></i> Add Location
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vehicle Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter Address"
                className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vehicle Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.vehicleType}
                  onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value })}
                  className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                  required
                >
                  <option value="">Select</option>
                  {vehicleTypes.map(t => (
                    <option key={t._id} value={t._id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Charges/KM <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.chargesKm}
                  onChange={(e) => setFormData({ ...formData, chargesKm: e.target.value })}
                  placeholder="Price"
                  min="0"
                  className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Charges/Day <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.chargesDay}
              onChange={(e) => setFormData({ ...formData, chargesDay: e.target.value })}
              placeholder="Charges/Day"
              min="0"
              className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Waiting Charges/Hour <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.waitingCharges}
              onChange={(e) => setFormData({ ...formData, waitingCharges: e.target.value })}
              placeholder="Waiting Charges"
              min="0"
              className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Shitting Capacity <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.sheets}
              onChange={(e) => setFormData({ ...formData, sheets: e.target.value })}
              className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              required
            >
              <option value="">Select Total Sheets</option>
              {Array.from({ length: 100 }, (_, i) => i + 1).map(i => (
                <option key={i} value={i}>{i}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Luggage Allowed/Person <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.luggage}
              onChange={(e) => setFormData({ ...formData, luggage: e.target.value })}
              className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              required
            >
              {[1, 2, 3, 4, 5].map(l => (
                <option key={l} value={`${l} Bags`}>{l} Bags</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Extra Charges/KM <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.extraCharges}
              onChange={(e) => setFormData({ ...formData, extraCharges: e.target.value })}
              placeholder="Charges after provided km"
              className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fuel Type</label>
            <select
              value={formData.fuelType}
              onChange={(e) => setFormData({ ...formData, fuelType: e.target.value })}
              className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              required
            >
              <option value="">Select</option>
              <option value="CNG">CNG</option>
              <option value="PETROL">PETROL</option>
              <option value="DIESEL">DIESEL</option>
              <option value="ELECTRIC">ELECTRIC</option>
              <option value="PETROL-CNG">PETROL-CNG</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">AC Available</label>
            <select
              value={formData.ac}
              onChange={(e) => setFormData({ ...formData, ac: e.target.value })}
              className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              required
            >
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Smoking Allowed</label>
            <select
              value={formData.smoking}
              onChange={(e) => setFormData({ ...formData, smoking: e.target.value })}
              className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              required
            >
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Amenities</label>
          <div className="relative" ref={amenitiesDropdownRef}>
            <div 
              onClick={() => setShowAmenitiesDropdown(!showAmenitiesDropdown)} 
              className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg cursor-pointer bg-white"
            >
              {getSelectedText('amenities', ['Sanitizer', 'Water', 'Mobile Charging', 'Newspaper', 'Music'])}
            </div>
            {showAmenitiesDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {['Sanitizer', 'Water', 'Mobile Charging', 'Newspaper', 'Music'].map(a => (
                  <div 
                    key={a} 
                    onClick={() => toggleSelection('amenities', a)} 
                    className="px-2 sm:px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                  >
                    <input 
                      type="checkbox" 
                      checked={formData.amenities.includes(a)} 
                      onChange={() => {}} 
                      className="mr-2" 
                    />
                    {a}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Driver & Vehicle Details</label>
          <JoditEditor
            value={formData.details}
            config={editorConfig}
            onBlur={newContent => setFormData({ ...formData, details: newContent })}
          />
        </div>

        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-4">Vehicle Gallery</h3>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload multiple vehicle images
          </label>
          <input
            type="file"
            multiple
            accept=".png,.jpg,.jpeg,.webp"
            onChange={handleGalleryChange}
            className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg"
          />
          
          <div className="grid grid-cols-5 gap-4 mt-4">
            {gallery.map((img, index) => (
              <div key={index} className="relative">
                <div
                  className="w-full h-36 rounded-lg border-2 border-gray-300"
                  style={{
                    backgroundImage: `url(${mediaUrl}${img})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeGalleryImage(img, index)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700"
                >
                  ×
                </button>
              </div>
            ))}
            {newGalleryFiles.map((file, index) => (
              <div key={`new-${index}`} className="relative">
                <div
                  className="w-full h-36 rounded-lg border-2 border-gray-300"
                  style={{
                    backgroundImage: `url(${URL.createObjectURL(file)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeNewGalleryImage(index)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={() => navigate('/view-vehicles')}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 text-sm font-medium text-white rounded-lg"
            style={{ backgroundColor: '#009ef7' }}
          >
            {submitting ? 'Please wait...' : 'Submit'}
          </button>
        </div>
      </form>

      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Add Location</h3>
              <button onClick={() => setShowLocationModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <form onSubmit={handleAddLocation} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  placeholder="Enter Location Name"
                  className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                  required
                />
              </div>
              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowLocationModal(false)}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 text-sm font-medium text-white rounded-lg"
                  style={{ backgroundColor: '#009ef7' }}
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditVehicle;
